import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Cpu, HardDrive, Users, PlayCircle, AlertCircle, CheckCircle } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './AdminPanel.css';

function AdminPanel() {
    const [systemStats, setSystemStats] = useState({
        totalUsers: 42,
        activeSessions: 7,
        totalGPUs: 4,
        availableGPUs: 2,
        cpuUsage: 45,
        memoryUsage: 62,
        storageUsed: 340,
        storageTotal: 1000
    });

    const [sessions, setSessions] = useState([]);
    const [gpuUsage, setGpuUsage] = useState([]);
    const [sessionHistory, setSessionHistory] = useState([]);

    useEffect(() => {
        // Fetch system stats
        fetchSystemStats();

        // Set up polling
        const interval = setInterval(fetchSystemStats, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchSystemStats = async () => {
        // TODO: Replace with real API calls
        // Simulated data for demonstration
        setSessions([
            { id: '1', user: 'alice@neuais.com', gpu: 0, runtime: '2h 15m', status: 'running', memory: '24GB/40GB' },
            { id: '2', user: 'bob@neuais.com', gpu: 1, runtime: '45m', status: 'running', memory: '18GB/40GB' },
            { id: '3', user: 'charlie@neuais.com', gpu: 3, runtime: '1h 32m', status: 'running', memory: '31GB/40GB' },
        ]);

        setGpuUsage([
            { gpu: 'GPU 0', utilization: 85, memory: 60 },
            { gpu: 'GPU 1', utilization: 45, memory: 45 },
            { gpu: 'GPU 2', utilization: 0, memory: 0 },
            { gpu: 'GPU 3', utilization: 78, memory: 78 },
        ]);

        setSessionHistory([
            { time: '00:00', sessions: 2 },
            { time: '04:00', sessions: 1 },
            { time: '08:00', sessions: 4 },
            { time: '12:00', sessions: 7 },
            { time: '16:00', sessions: 5 },
            { time: '20:00', sessions: 3 },
        ]);
    };

    const terminateSession = async (sessionId) => {
        if (confirm('Are you sure you want to terminate this session?')) {
            // TODO: API call to terminate
            console.log('Terminating session:', sessionId);
            setSessions(sessions.filter(s => s.id !== sessionId));
        }
    };

    const getStatusColor = (value, thresholds = [50, 80]) => {
        if (value < thresholds[0]) return 'var(--accent-success)';
        if (value < thresholds[1]) return 'var(--accent-warning)';
        return 'var(--accent-danger)';
    };

    const COLORS = ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B'];

    return (
        <div className="admin-panel-page">
            <div className="container">
                <div className="admin-header">
                    <div>
                        <h1>System Monitoring</h1>
                        <p>Real-time overview of platform resources and sessions</p>
                    </div>
                    <div className="header-actions">
                        <Link to="/dashboard" className="btn btn-secondary">← Back to Dashboard</Link>
                    </div>
                </div>

                {/* System Overview Cards */}
                <div className="monitor-grid">
                    <div className="monitor-card glass-card">
                        <div className="card-header">
                            <Users size={24} style={{ color: 'var(--accent-primary)' }} />
                            <h3>Users</h3>
                        </div>
                        <div className="card-stat">
                            <div className="stat-main">{systemStats.totalUsers}</div>
                            <div className="stat-sub">Total Registered</div>
                        </div>
                    </div>

                    <div className="monitor-card glass-card">
                        <div className="card-header">
                            <PlayCircle size={24} style={{ color: 'var(--accent-success)' }} />
                            <h3>Active Sessions</h3>
                        </div>
                        <div className="card-stat">
                            <div className="stat-main">{systemStats.activeSessions}</div>
                            <div className="stat-sub">Currently Running</div>
                        </div>
                    </div>

                    <div className="monitor-card glass-card">
                        <div className="card-header">
                            <Cpu size={24} style={{ color: 'var(--accent-secondary)' }} />
                            <h3>GPU Availability</h3>
                        </div>
                        <div className="card-stat">
                            <div className="stat-main">
                                {systemStats.availableGPUs} / {systemStats.totalGPUs}
                            </div>
                            <div className="stat-sub">GPUs Available</div>
                        </div>
                    </div>

                    <div className="monitor-card glass-card">
                        <div className="card-header">
                            <HardDrive size={24} style={{ color: 'var(--accent-warning)' }} />
                            <h3>Storage</h3>
                        </div>
                        <div className="card-stat">
                            <div className="stat-main">{systemStats.storageUsed}GB</div>
                            <div className="stat-sub">of {systemStats.storageTotal}GB Used</div>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{
                                        width: `${(systemStats.storageUsed / systemStats.storageTotal) * 100}%`,
                                        background: getStatusColor((systemStats.storageUsed / systemStats.storageTotal) * 100)
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Row */}
                <div className="charts-row">
                    <div className="chart-card glass-card">
                        <h3>Session Activity (24h)</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={sessionHistory}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="time" stroke="rgba(255,255,255,0.5)" />
                                <YAxis stroke="rgba(255,255,255,0.5)" />
                                <Tooltip
                                    contentStyle={{ background: '#1a1d26', border: '1px solid rgba(255,255,255,0.1)' }}
                                />
                                <Line type="monotone" dataKey="sessions" stroke="#8B5CF6" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="chart-card glass-card">
                        <h3>GPU Utilization</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={gpuUsage}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="gpu" stroke="rgba(255,255,255,0.5)" />
                                <YAxis stroke="rgba(255,255,255,0.5)" />
                                <Tooltip
                                    contentStyle={{ background: '#1a1d26', border: '1px solid rgba(255,255,255,0.1)' }}
                                />
                                <Bar dataKey="utilization" fill="#06B6D4" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Active Sessions Table */}
                <div className="sessions-table glass-card">
                    <h3>Active Sessions</h3>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>GPU</th>
                                    <th>Runtime</th>
                                    <th>Memory</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.map((session) => (
                                    <tr key={session.id}>
                                        <td>{session.user}</td>
                                        <td>GPU {session.gpu}</td>
                                        <td>{session.runtime}</td>
                                        <td>{session.memory}</td>
                                        <td>
                                            <span className="status-badge status-running">
                                                <CheckCircle size={14} />
                                                {session.status}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => terminateSession(session.id)}
                                                className="btn-small btn-danger"
                                            >
                                                Terminate
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* System Health Indicators */}
                <div className="health-grid">
                    <div className="health-card glass-card">
                        <div className="health-header">
                            <Activity size={20} />
                            <h4>CPU Usage</h4>
                        </div>
                        <div className="health-meter">
                            <div className="meter-value">{systemStats.cpuUsage}%</div>
                            <div className="meter-bar">
                                <div
                                    className="meter-fill"
                                    style={{
                                        width: `${systemStats.cpuUsage}%`,
                                        background: getStatusColor(systemStats.cpuUsage)
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div className="health-card glass-card">
                        <div className="health-header">
                            <HardDrive size={20} />
                            <h4>Memory Usage</h4>
                        </div>
                        <div className="health-meter">
                            <div className="meter-value">{systemStats.memoryUsage}%</div>
                            <div className="meter-bar">
                                <div
                                    className="meter-fill"
                                    style={{
                                        width: `${systemStats.memoryUsage}%`,
                                        background: getStatusColor(systemStats.memoryUsage)
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminPanel;
